import { useEffect, useState } from "react";
import { Instagram, ArrowUpRight, ExternalLink, Film, Layers } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { org } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MediaBadge = ({ type }) => {
  if (type === "VIDEO") return <Film size={12} className="text-white drop-shadow" />;
  if (type === "CAROUSEL_ALBUM") return <Layers size={12} className="text-white drop-shadow" />;
  return null;
};

function PostSkeleton({ i }) {
  return (
    <div className="aspect-square rounded-2xl bg-iyaf-paper animate-pulse" data-testid={`ig-skeleton-${i}`} />
  );
}

export default function InstagramFeed() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/instagram/feed?limit=6`, { timeout: 12000 });
        if (cancelled) return;
        const items = (data.items || []).filter((p) => p.image);
        setPosts(items);
        setStatus(items.length ? "ok" : "error");
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="py-24 bg-white border-y border-black/5" data-testid="home-instagram">
      <div className="container-iyaf">
        <div className="flex flex-col lg:flex-row gap-8 lg:items-end justify-between mb-12">
          <div className="max-w-2xl">
            <p className="eyebrow text-iyaf-blue mb-4 flex items-center gap-2">
              <Instagram size={14} /> {t("instagram.eyebrow")}
            </p>
            <h2 className="h2 text-balance">@iyaf.global</h2>
            <p className="mt-4 font-editorial text-2xl text-iyaf-ink/70 leading-snug">
              {t("instagram.lead")}
            </p>
          </div>
          <a
            href={org.socials.instagram}
            target="_blank"
            rel="noreferrer"
            data-testid="instagram-follow-cta"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-iyaf-ink text-white text-sm font-semibold hover:bg-iyaf-blue transition self-start"
          >
            {t("cta.follow")} <ArrowUpRight size={15} />
          </a>
        </div>

        {status === "loading" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ig-loading">
            {Array.from({ length: 6 }).map((_, i) => <PostSkeleton key={i} i={i} />)}
          </div>
        )}

        {status === "error" && (
          <div className="text-center py-14 bg-iyaf-paper rounded-3xl border border-black/5" data-testid="ig-error">
            <Instagram size={28} className="mx-auto text-iyaf-ink/40 mb-3" />
            <p className="font-display font-black text-xl">{t("instagram.errorTitle")}</p>
            <p className="mt-2 text-sm text-iyaf-ink/65 max-w-md mx-auto">
              {t("instagram.errorBody")}
            </p>
            <a href={org.socials.instagram} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-iyaf-ink text-white text-sm font-semibold hover:bg-iyaf-blue transition" data-testid="ig-error-cta">
              {t("cta.viewProfile")} <ArrowUpRight size={14} />
            </a>
          </div>
        )}

        {status === "ok" && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ig-grid">
            {posts.map((p, i) => (
              <motion.a
                key={p.id}
                href={p.permalink || org.socials.instagram}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="group relative aspect-square rounded-2xl overflow-hidden bg-iyaf-paper"
                data-testid={`ig-post-${i}`}
                title={p.snippet}
              >
                <img
                  src={p.image}
                  alt={p.snippet || "Instagram post"}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
                />
                {/* Media type badge (top-right) */}
                <div className="absolute top-2 right-2">
                  <MediaBadge type={p.media_type} />
                </div>
                {/* Hover overlay with caption snippet */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                  <div className="text-white">
                    <p className="text-[11px] line-clamp-3 leading-snug">{p.snippet}</p>
                    <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold opacity-85">
                      {t("instagram.viewOnInstagram")} <ExternalLink size={9} />
                    </span>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        )}

        <p className="text-center mt-8 text-xs text-iyaf-ink/40">
          {t("instagram.syncNote")}
        </p>
      </div>
    </section>
  );
}
